import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useLocation } from "react-router";
import Countdown from 'react-countdown';
import Modal from "components/Modal";
import { useTranslation } from 'react-i18next';
import { Location } from 'history';
import stringUtils from 'common/stringUtils';
import { clickRailplusBalanceWeb, clickRailplusChargeWeb, clickRailplusEventWeb, clickRailplusHistoryWeb, clickRailplusHomepageWeb, clickRailplusMenuWeb } from 'common/appInterface';
import { getVersionUrl } from 'common/cacheOverwrite';

const RailplusGuide = forwardRef<any, CardTabProps>(({ cardInfo, swiperIndex, swiper, swiperTabChangeEventHandle }, ref) => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** 다국어처리 */
  const { t } = useTranslation();

  const [trafficCardNo, setTrafficCardNo] = useState<string>();
  const [trafficBalance, setTrafficBalance] = useState<string>();
  const [transactionList, setTransactionList] = useState<TrafficTransaction[]>();

  const [isCountdownModal, setCountdownModal] = useState(false);
  const [isRetryModal, setRetryModal] = useState(false);
  const [isBalanceTag, setBalanceTag] = useState(false);
  const [isHistoryTag, setHistoryTag] = useState(false);

  /** 카운트다운 모달닫기 */
  const closeCountdownModal = useCallback(() => {
    setCountdownModal(false);
  }, []);

  /** 재시도 모달닫기 */
  const closeRetryModal = useCallback(() => {
    setRetryModal(false);
  }, []);

  /** 시간초과 */
  const callCountdownTimeout = useCallback(() => {
    setCountdownModal(false);
    setRetryModal(true);
  }, []);

  /** 다시시도 */
  const clickRetry = useCallback(() => {
    setRetryModal(false);
    setCountdownModal(true);
  }, []);

  /** Tag Ready */
  const clickTagReady = useCallback(() => {
    // 아이폰은 잔액조회 처리
    if (location.state.query.deviceType === "1") {
      clickRailplusBalanceWeb(cardInfo.trafficCardNo);
    }
    // 안드로이드는 전체조회 처리
    else {
      clickRailplusEventWeb(cardInfo.trafficCardNo);
    }
  }, [cardInfo.trafficCardNo, location.state.query.deviceType]);

  /** 교통카드 이용내역 조회 */
  const clickRailplusHistory = useCallback(() => {
    clickRailplusHistoryWeb(cardInfo.trafficCardNo);
  }, [cardInfo.trafficCardNo]);

  /** 교통카드 데이터 처리 (잔액) */
  const setRailplusDataBalance = useCallback((trafficCardNo: string, trafficBalance: string) => {
    setBalanceTag(true);
    setTrafficCardNo(trafficCardNo);
    setTrafficBalance(trafficBalance);
  }, []);

  /** 교통카드 데이터 처리 (이용내역) */
  const setRailplusDataHistory = useCallback((transactionList: string) => {
    setHistoryTag(true);
    setTransactionList(JSON.parse(transactionList));
  }, []);

  /** 교통카드 데이터 처리 (전체) */
  const setRailplusDataAll = useCallback((trafficCardNo: string, trafficBalance: string, transactionList: string) => {
    setRailplusDataBalance(trafficCardNo, trafficBalance);
    setRailplusDataHistory(transactionList);
  }, [setRailplusDataBalance, setRailplusDataHistory]);

  /** 교통카드 이용내역 결과전달 (completedRailplus_App (APP > WEB)) */
  const callbackCompletedRailplus = useCallback((event) => {
    if (swiper.activeIndex === swiperIndex) setRailplusDataAll(event.detail.trafficCardNo, event.detail.trafficBalance, decodeURIComponent(event.detail.transactionList));
  }, [setRailplusDataAll, swiper, swiperIndex]);

  /** 교통카드 준비응답 (callbackRailplusReady_App (APP > WEB)) */
  const callbackRailplusReady = useCallback(() => {
    if (swiper.activeIndex === swiperIndex) setCountdownModal(true);
  }, [swiper, swiperIndex]);

  /** 교통카드 잔액 결과전달 (iOS) (callbackRailplusBalance_App (APP > WEB)) */
  const callbackRailplusBalance = useCallback((event) => {
    if (swiper.activeIndex === swiperIndex) setRailplusDataBalance(event.detail.trafficCardNo, event.detail.trafficBalance);
  }, [setRailplusDataBalance, swiper, swiperIndex]);

  /** 교통카드 이용내역 결과전달 (iOS) (callbackRailplusHistory_App (APP > WEB)) */
  const callbackRailplusHistory = useCallback((event) => {
    if (swiper.activeIndex === swiperIndex) setRailplusDataHistory(decodeURIComponent(event.detail.transactionList));
  }, [setRailplusDataHistory, swiper, swiperIndex]);

  // 메뉴클릭 이벤트 처리
  useImperativeHandle(ref, () => ({
    click() {
      clickTagReady();
    }
  }), [clickTagReady]);

  // 최초실행
  useEffect(() => {
    // console.log("QUERY STATE");
    // console.log(location.state);

    if(swiper){
      clickRailplusMenuWeb();
    }

    // Swiper Height Resize
    swiperTabChangeEventHandle();

    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 교통카드 이용내역 결과전달 이벤트 리스너 (completedRailplus_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("railplus", callbackCompletedRailplus);
    return () => document.removeEventListener("railplus", callbackCompletedRailplus);
  }, [location, callbackCompletedRailplus]);

  // 교통카드 준비응답 이벤트 리스너 (callbackRailplusReady_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("railplusReady", callbackRailplusReady);
    return () => document.removeEventListener("railplusReady", callbackRailplusReady);
  }, [callbackRailplusReady]);

  // 교통카드 잔액 결과전달 (iOS) 이벤트 리스너 (callbackRailplusBalance_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("railplusBalance", callbackRailplusBalance);
    return () => document.removeEventListener("railplusBalance", callbackRailplusBalance);
  }, [callbackRailplusBalance]);

  // 교통카드 이용내역 결과전달 (iOS) 이벤트 리스너 (callbackRailplusHistory_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("railplusHistory", callbackRailplusHistory);
    return () => document.removeEventListener("railplusHistory", callbackRailplusHistory);
  }, [callbackRailplusHistory]);

  return (
    <>
      {!isBalanceTag &&
        <section className="contents myPageSetting pb64-mb">
          <div className="inner">
            <div className="tmoneyWrap">
              {/*<div className="picto" onClick={clickTagReady}>*/}
              {/*  <img src={location.state.query.deviceType === "1" ? "/assets/images/content/nfc_touch_ios.gif" : "/assets/images/content/nfc_touch_and.gif"} alt="Android폰" />*/}
              {/*</div>*/}
              {/*location.state.query.deviceType === "2" && <p className="noticeTxt">{t("TRAF01.explan06")}</p>}
                <p className={location.state.query.deviceType === "1" ? "noticeTxt" : "noticeTxt mgt15"}>{t("TRAF01.explan02")}</p>*/}
                <p className="noticeTitle mgb16">{t('TRAF01.explan06', 'Transportation Card')}</p>
                <p className="noticeTxt">{t('TRAF01.explan02')}</p>
                <p className="infoTxt mgt5">{location.state.query.deviceType === '1' ? t('TRAF01.explan04') : t('TRAF01.explan03')}</p>
                <div className="btnWrapTy05 mgt16">
                  <button type="button" className="btnPrimaryTy01" onClick={clickRailplusChargeWeb}>
                    <img style={{ height: '1.0rem', width: '1.0rem', flexShrink: 0, marginRight: '4px' }}
                         src={getVersionUrl('/assets/images/ico/ic_topup_24.png')} alt="topup icon" />
                    <span>{t('TRAF01.button04')}</span>
                  </button>
                </div>
              <div className="btnWrapTy05 mgt16">
                <button type="button" className="btnPrimaryTy03" onClick={clickTagReady}>
                  <img style={{ height: '1.0rem', width: '1.0rem', flexShrink: 0, marginRight: '4px' }}
                       src={getVersionUrl('/assets/images/ico/ic_view_balance_24.png')} alt="balance icon" />
                  <span>{t('TRAF01.button03')}</span>
                </button>
              </div>
              <p className="tcard_num mgt16">
                <span>{t('TRAF01.explan01')}</span>{stringUtils.cardNoFormat(cardInfo.trafficCardNo, ' ')}</p>
              <p className="infoTxt mgt16">※ {t('TRAF01.explan05')}</p>
              <button type="button" className="btn_link mgt15" onClick={clickRailplusHomepageWeb}>
                <span>{t('TRAF01.button01')}</span></button>
            </div>
          </div>
        </section>
      }
      {isBalanceTag &&
        <section className="contents tmoneyListWrap pb64-mb">
          <div className="inner">
            <div className="tmoneyWrap">
              <button type="button" className="add_btn"
                      onClick={clickRailplusChargeWeb}>+ {t('TRAF02.button01')}</button>
              <p className="krw"><span>{stringUtils.moneyFormat(trafficBalance)}</span> KRW</p>
              <p className="card_num"><span>{t("TRAF02.explan01")}</span>{stringUtils.cardNoFormat(trafficCardNo, " ")}</p>
            </div>
            <div className="settingListWrap clfix">
              <dl className="settingList">
                <dt>
                  <span className="day">{t("TRAF02.explan02")}</span>
                </dt>
                {!isHistoryTag &&
                  <div className="tmoneyWrap">
                    <button type="button" className="add_btn" onClick={clickRailplusHistory}>{t("TRAF02.button02")}</button>
                  </div>
                }
                {isHistoryTag && transactionList && transactionList.map((value, index) =>
                  <dd key={index}>
                    <button type="button" style={{padding: '3px 20px'}}>
                      <span className="spot" style={{fontSize: '0.8rem'}}>{value.type === "DEBIT" ? t("TRAF02.explan05") : t("TRAF02.explan04")}</span>
                      <span className={value.type === "DEBIT" ? "place out" : "place"} style={{fontSize: '0.8rem'}}>{value.type === "CREDIT" && "+"}{stringUtils.moneyFormat(value.amount)} KRW</span>
                    </button>
                  </dd>
                )}
              </dl>
            </div>
          </div>
        </section>
      }
      {/* 카운트다운 */}
      {/*{isCountdownModal && <RailplusCountdownModal trafficCardNo={cardInfo.trafficCardNo} isCountdown={isCountdownModal} closeCountdown={closeCountdownModal} onTimeout={callCountdownTimeout} onSuccess={setRailplusDataAll} swiper={swiper} swiperIndex={swiperIndex} />}*/}
      {isCountdownModal && <RailplusCountdownModal trafficCardNo={cardInfo.trafficCardNo} isCountdown={isCountdownModal} closeCountdown={closeCountdownModal} onTimeout={callCountdownTimeout} swiper={swiper} swiperIndex={swiperIndex} />}
      {/* 재시도 */}
      <Modal isOpen={isRetryModal} contentLabel="modal" onRequestClose={closeRetryModal} className="popTyLayer2" overlayClassName="dim" closeTimeoutMS={500}>
        <p className="message">{t("TRAF01.popup04")}</p>
        <p className="subTxt mgt20">{t("TRAF01.popup05")}</p>
        <p className="subTxt mgt20">※ {t("TRAF01.popup06")}<br />※ {t("TRAF01.popup07")}</p>
        <p className="messageARS">{t("TRAF01.popup08")}</p>
        <div className="btnWrapTy02">
          <button type="button" className="btnPrimaryTy03" onClick={closeRetryModal}><span>{t("COMMON.button.no")}</span></button>
          <button type="button" className="btnPrimaryTy01" onClick={clickRetry}><span>{t("TRAF01.button02")}</span></button>
        </div>
      </Modal>
    </>
  );
});

/** 교통카드인식 카운트다운 */
// const RailplusCountdownModal: React.FC<RailplusCountdown> = ({ trafficCardNo, isCountdown, closeCountdown, onTimeout, onSuccess, swiper, swiperIndex }) => {
const RailplusCountdownModal: React.FC<RailplusCountdown> = ({ trafficCardNo, isCountdown, closeCountdown, onTimeout, swiper, swiperIndex }) => {
  /** 다국어처리 */
  const { t } = useTranslation();

  /** 교통카드 이용내역 결과전달 (completedRailplus_App (APP > WEB)) */
  const callbackCompletedRailplus = useCallback(() => {
    if (swiper.activeIndex === swiperIndex) {
      closeCountdown();
      // onSuccess(event.detail.trafficCardNo, event.detail.trafficBalance, decodeURIComponent(event.detail.transactionList));
    }
  // }, [closeCountdown, onSuccess, swiper.activeIndex, swiperIndex]);
  }, [closeCountdown, swiper.activeIndex, swiperIndex]);

  // TODO 테스트호출
  const testCall = useCallback(() => {
    let jsonValue = "[{\"type\":\"DEBIT\",\"amount\":1250},{\"type\":\"DEBIT\",\"amount\":1250},{\"type\":\"DEBIT\",\"amount\":1250},{\"type\":\"DEBIT\",\"amount\":1250},{\"type\":\"DEBIT\",\"amount\":1250},{\"type\":\"CREDIT\",\"amount\":30000},{\"type\":\"DEBIT\",\"amount\":1250},{\"type\":\"DEBIT\",\"amount\":1250},{\"type\":\"DEBIT\",\"amount\":1250}]";
    let event = new CustomEvent("railplus", { detail: { trafficCardNo: "1234123412341234", trafficBalance: "123456", transactionList: jsonValue }});
    document.dispatchEvent(event);
  }, []);

  // 교통카드 이용내역 결과전달 이벤트 리스너 (completedRailplus_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("railplus", callbackCompletedRailplus);
    return () => document.removeEventListener("railplus", callbackCompletedRailplus);
  }, [callbackCompletedRailplus]);

  return (
    <Modal isOpen={isCountdown} contentLabel="modal" onRequestClose={closeCountdown} className="popTyLayer3" overlayClassName="dim" closeTimeoutMS={500} onAfterOpen={() => clickRailplusEventWeb(trafficCardNo)}>
      <p className="message">{t("TRAF01.popup01")}</p>
      <div className="timer">
        {isCountdown ? <img src={"/assets/images/content/sandtimer2.gif?" + Date.now()} alt="타이머" /> : null}
      </div>
      <p className="message2">{t("TRAF01.popup02")}</p>
      <div className="btnWrapTy02 mgt50 w120 btn-center"><button type="button" className="btnPrimaryTy02" onClick={closeCountdown}><span>{t("TRAF01.popup09")}</span></button></div>
      <Countdown date={Date.now() + (60 * 1000)} renderer={() => null} onComplete={onTimeout} />
      {process.env.REACT_APP_PROFILE !== "prod" &&
        <button className="btnPrimaryTy01" type="button" onClick={() => testCall()}><span>읽기</span></button>
      }
    </Modal>
  );
}

export default RailplusGuide;